import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { UserCheck, Search, Filter, Download, Calendar, Mail, Phone, Eye } from 'lucide-react';
import { format } from 'date-fns';

interface EarlyAdopterSignup {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  primary_interest: string;
  urgency_level: string;
  phone: string;
  message: string;
  source_page: string;
  status: string;
  created_at: string;
  updated_at: string;
  contacted_at?: string;
}

export default function AdminFormsEarlyAdopters() {
  const { isAdmin, isLoading: authLoading, user } = useAdminAuth();
  const [signups, setSignups] = useState<EarlyAdopterSignup[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [retryCount, setRetryCount] = useState(0);
  const [selectedSignup, setSelectedSignup] = useState<EarlyAdopterSignup | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  useEffect(() => {
    if (!authLoading && isAdmin && user) {
      fetchSignups();
    }
  }, [authLoading, isAdmin, user]);

  // Support drill-down via query params like ?status=&role=&q=
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const status = params.get('status');
      const role = params.get('role');
      const q = params.get('q');
      if (status) setStatusFilter(status);
      if (role) setRoleFilter(role);
      if (q) setSearchTerm(q);
    } catch {}
  }, []);

  const fetchSignups = async () => {
    if (!isAdmin || !user) {
      console.log('Not authenticated as admin, skipping fetch');
      setLoading(false);
      return;
    }

    try {
      console.log('Fetching early adopter signups with auth:', { userId: user.id, isAdmin });
      
      const { data, error } = await supabase.rpc('list_early_adopter_signups_admin');

      if (error) {
        console.error('Supabase error:', error);
        
        // Retry logic for auth context issues
        if (error.code === 'PGRST301' && retryCount < 3) {
          console.log(`Retrying fetch (attempt ${retryCount + 1})`);
          setRetryCount(prev => prev + 1);
          setTimeout(() => fetchSignups(), 1000 * (retryCount + 1));
          return;
        }
        
        throw error;
      }
      
      // The RPC now returns the array directly
      const result = data as any;
      console.log('Raw result from database:', result);
      
      let processedSignups = [];
      if (Array.isArray(result)) {
        processedSignups = result;
      } else {
        processedSignups = [];
      }
      
      // Filter out null entries
      processedSignups = processedSignups.filter(signup => signup && signup.id);
      console.log('Successfully fetched signups:', processedSignups.length);
      setSignups(processedSignups);
      setRetryCount(0);
    } catch (error) {
      console.error('Error fetching early adopter signups:', error);
      setSignups([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredSignups = signups.filter(signup => {
    const matchesSearch = 
      signup.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      signup.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      signup.last_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || signup.status === statusFilter;
    const matchesRole = roleFilter === 'all' || signup.role === roleFilter;
    
    return matchesSearch && matchesStatus && matchesRole;
  });

  const getUrgencyBadge = (urgency: string) => {
    const variants = {
      'immediate': 'destructive',
      'soon': 'default',
      'exploring': 'secondary'
    } as const;
    
    return <Badge variant={variants[urgency as keyof typeof variants] || 'secondary'}>{urgency}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      'pending': 'secondary',
      'contacted': 'default',
      'converted': 'default'
    } as const;
    
    return <Badge variant={variants[status as keyof typeof variants] || 'secondary'}>{status}</Badge>;
  };

  const handleViewSignup = (signup: EarlyAdopterSignup) => {
    setSelectedSignup(signup);
    setIsViewModalOpen(true);
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Early Adopter Signups</h1>
          <p className="text-muted-foreground">
            Manage and track early adopter interest and sign-ups
          </p>
        </div>
        <Button>
          <Download className="mr-2 h-4 w-4" />
          Export
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Signups</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{signups.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Week</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {signups.filter(s => new Date(s.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Immediate Need</CardTitle>
            <Badge variant="destructive" className="h-4 w-4 p-0"></Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {signups.filter(s => s.urgency_level === 'immediate').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {signups.length > 0 ? Math.round((signups.filter(s => s.status === 'converted').length / signups.length) * 100) : 0}%
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filter Signups</CardTitle>
          <CardDescription>Search and filter early adopter signups</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
              </SelectContent>
            </Select>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="older_adult">Older Adult</SelectItem>
                <SelectItem value="family_member">Family Member</SelectItem>
                <SelectItem value="caregiver">Caregiver</SelectItem>
                <SelectItem value="professional">Professional</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Signups Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Signups ({filteredSignups.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Interest</TableHead>
                <TableHead>Urgency</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSignups.map((signup) => (
                <TableRow key={signup.id}>
                  <TableCell className="font-medium">
                    {signup.first_name} {signup.last_name}
                    {signup.phone && (
                      <div className="flex items-center text-xs text-muted-foreground">
                        <Phone className="mr-1 h-3 w-3" />
                        {signup.phone}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <Mail className="mr-1 h-3 w-3" />
                      {signup.email}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{signup.role}</Badge>
                  </TableCell>
                  <TableCell>{signup.primary_interest}</TableCell>
                  <TableCell>{getUrgencyBadge(signup.urgency_level)}</TableCell>
                  <TableCell>{getStatusBadge(signup.status)}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="text-xs">
                      {signup.source_page || 'Unknown'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {format(new Date(signup.created_at), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewSignup(signup)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredSignups.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No signups found matching your criteria
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Signup Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Early Adopter Signup Details</DialogTitle>
            <DialogDescription>
              Complete information for this signup submission
            </DialogDescription>
          </DialogHeader>
          
          {selectedSignup && (
            <div className="space-y-6">
              {/* Personal Information */}
              <div className="grid gap-4">
                <h3 className="text-lg font-semibold border-b pb-2">Personal Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">First Name</label>
                    <p className="font-medium">{selectedSignup.first_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Last Name</label>
                    <p className="font-medium">{selectedSignup.last_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email</label>
                    <p className="font-medium">{selectedSignup.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Phone</label>
                    <p className="font-medium">{selectedSignup.phone || 'Not provided'}</p>
                  </div>
                </div>
              </div>

              {/* Interest Information */}
              <div className="grid gap-4">
                <h3 className="text-lg font-semibold border-b pb-2">Interest Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Role</label>
                    <Badge variant="outline">{selectedSignup.role}</Badge>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Urgency Level</label>
                    {getUrgencyBadge(selectedSignup.urgency_level)}
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-muted-foreground">Primary Interest</label>
                    <p className="font-medium">{selectedSignup.primary_interest}</p>
                  </div>
                  {selectedSignup.message && (
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-muted-foreground">Additional Message</label>
                      <p className="font-medium bg-muted p-3 rounded-md">{selectedSignup.message}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Administrative Information */}
              <div className="grid gap-4">
                <h3 className="text-lg font-semibold border-b pb-2">Administrative Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Status</label>
                    {getStatusBadge(selectedSignup.status)}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Source Page</label>
                    <Badge variant="secondary" className="text-xs">
                      {selectedSignup.source_page || 'Unknown'}
                    </Badge>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Signup Date</label>
                    <p className="font-medium">{format(new Date(selectedSignup.created_at), 'PPP')}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Last Updated</label>
                    <p className="font-medium">{format(new Date(selectedSignup.updated_at), 'PPP')}</p>
                  </div>
                  {selectedSignup.contacted_at && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Contacted On</label>
                      <p className="font-medium">{format(new Date(selectedSignup.contacted_at), 'PPP')}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setIsViewModalOpen(false)}>
                  Close
                </Button>
                <Button onClick={() => window.open(`mailto:${selectedSignup.email}?subject=SeniorThrive - Following up on your signup`)}>
                  <Mail className="h-4 w-4 mr-2" />
                  Contact User
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}